import { IconButton } from '@mui/material';
import { SidebarDrawer, SidebarItem } from '@treedis/tour-loader';
import { Icon } from 'components-new';
import { useCallback, useState } from 'react';
import { bottomBarOpenState, forceShowPathSwitchMobileModalState, useSetRecoilState } from 'store';
import { GameStatus } from '@/components/types';
import { Game } from '@/_models/Game';
import { Tour } from '@/_models/Tour';
import { Backpack } from './components/backpack/Backpack';
import { Book } from './components/book/Book';
import { Settings } from './components/settings/Settings';
import GameAnalyticService from '@/components/tour/game/services/GameAnalyticService';
import { WhiteLabel } from '@/_models/Common';
import { Participants } from './components/participants/Participants';

type Props = {
  initialDuplicates: any[];
  whiteLabel: WhiteLabel;
  tour: Tour;
  translation: Record<string, string>;
  username: string;
  game: Game;
  analytic: GameAnalyticService;
  gameStatus: GameStatus;
  gamePoints: number;
  backpackNewItems: number;
  showBackpack: boolean;
  showBook: boolean;
  showPathSwitch: boolean;
  setBaseState: (obj: Record<string, unknown>) => void;
  onEndExperience: () => void;
  // Hub
  showParticipantsIcon: boolean;
  showParticipantsReel: boolean;
  // audio
  mainPlaying;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Sidebar({
  initialDuplicates,
  tour,
  whiteLabel,
  translation,
  username,
  game,
  analytic,
  gameStatus,
  gamePoints,
  backpackNewItems,
  onEndExperience,
  showBackpack,
  showBook,
  setBaseState,
  showParticipantsIcon,
  showParticipantsReel,
  mainPlaying,
  showPathSwitch,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const setBbisOpen = useSetRecoilState(bottomBarOpenState);
  const toggleSidebar = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);
  const setForceShowPathSwitchMobileModal = useSetRecoilState(forceShowPathSwitchMobileModalState);

  const handleBookToggle = useCallback(() => {
    setBaseState({ showBook: !showBook });
  }, [setBaseState, showBook]);

  const handleParticipantsToggle = useCallback(() => {
    const value = !showParticipantsReel;
    setBaseState({ showParticipantsReel: value, showBackpack: false });
    setBbisOpen(value);
  }, [setBaseState, setBbisOpen, showParticipantsReel]);

  const handleBackpackToggle = useCallback(() => {
    const value = !showBackpack;
    setBaseState({ showBackpack: value, showParticipantsReel: false });
    setBbisOpen(value);
  }, [setBaseState, setBbisOpen, showBackpack]);

  return (
    <>
      <IconButton
        size="small"
        style={{ top: 'calc(20% - 35px)' }}
        sx={{
          right: '15px',
          position: 'fixed',
          zIndex: 1,
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(15px)',
          transform: isOpen ? 'rotate(180deg)' : undefined,
          transition: 'all .3s',
          '&:hover, &.active': {
            backgroundColor: 'rgba(256,256,256, .2)',
          },
        }}
        onClick={toggleSidebar}
      >
        <Icon icon="fa-chevron-left" size="small" />
      </IconButton>
      <SidebarDrawer
        variant="permanent"
        anchor="right"
        open={isOpen}
        sx={{
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Settings
          isDrawerOpen={isOpen}
          initialDuplicates={initialDuplicates}
          tour={tour}
          translation={translation}
          username={username}
          game={game}
          analytic={analytic}
          gameStatus={gameStatus}
          gamePoints={gamePoints}
          onEndExperience={onEndExperience}
          whiteLabel={whiteLabel}
          mainPlaying={mainPlaying}
        />
        {game?.gameBookOn && (
          <Book isDrawerOpen={isOpen} onToggle={handleBookToggle} translation={translation} />
        )}
        {game?.backpackOn && (
          <div style={{ position: 'relative' }}>
            <Backpack
              isDrawerOpen={isOpen}
              onToggle={handleBackpackToggle}
              translation={translation}
            />
            {backpackNewItems > 0 && (
              <div
                className="exp-bp-new"
                style={{
                  width: '8px',
                  height: '8px',
                  background: 'red',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '7px',
                  right: '3px',
                }}
              />
            )}
          </div>
        )}
        {showPathSwitch && (
          <SidebarItem
            isOpen={isOpen}
            popupLabel={translation.selectPath || 'Select path'}
            button={{
              label: translation.selectPath || 'Select path',
              tooltip: translation.selectPath || 'Select path',
              icon: 'fas fa-route',
              onClick: () => setForceShowPathSwitchMobileModal((e) => !e),
            }}
          />
        )}
        {showParticipantsIcon && (
          <Participants
            isDrawerOpen={isOpen}
            onToggle={handleParticipantsToggle}
            translation={translation}
          />
        )}
      </SidebarDrawer>
    </>
  );
}
